import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/atoms/card";

interface TimeWidgetProps {
  hours?: number;
  minutes: number;
  seconds: number;
  title: string;
}

export default function TimeWidget({
  hours = 0,
  minutes,
  seconds,
  title,
}: TimeWidgetProps) {
  // Calculate the total seconds and the percentage for the progress bar
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  // Adjust max value based on whether we have hours
  const maxValue =
    hours > 0 ? Math.max(hours + 1, 2) * 3600 : Math.max(minutes, 60) * 60;
  const percentage = Math.min(
    100,
    Math.max(0, (totalSeconds / maxValue) * 100),
  );

  // Determine the color for the indicator dot
  let indicatorColor = "bg-green-500";
  if (percentage > 66) {
    indicatorColor = "bg-red-500";
  } else if (percentage > 33) {
    indicatorColor = "bg-yellow-400";
  }

  // Format time display
  const formatTime = () => {
    if (hours > 0) {
      return {
        mobile: `${hours}h ${minutes}m`,
        desktop: `${hours}h ${minutes}m ${seconds}s`,
      };
    }
    // Si no hay minutos, solo mostrar segundos
    if (minutes === 0) {
      return {
        mobile: `${seconds}s`,
        desktop: `${seconds}sec`,
      };
    }
    return {
      mobile: `${minutes}m ${seconds}s`,
      desktop: `${minutes}m ${seconds}sec`,
    };
  };

  const timeDisplay = formatTime();

  // Format scale labels
  const getScaleLabel = () => {
    if (hours > 0) {
      const maxHours = Math.ceil(maxValue / 3600);
      return {
        middle: `${Math.floor(maxHours / 2)}h`,
        max: `${maxHours}h`,
      };
    }
    const maxMinutes = Math.ceil(maxValue / 60);
    return {
      middle: `${Math.floor(maxMinutes / 2)}m`,
      max: `${maxMinutes}m`,
    };
  };

  const scaleLabels = getScaleLabel();

  return (
    <Card className="h-full bg-white w-full min-w-0">
      <CardHeader className="pb-2">
        <CardTitle
          className="text-sm lg:text-xs font-medium text-gray-700 truncate"
          title={title}
        >
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center justify-between md:items-start md:flex-col gap-2 min-w-0">
          <p className="text-lg sm:text-xl lg:text-2xl font-bold truncate min-w-0 flex-1">
            <span className="sm:hidden">{timeDisplay.mobile}</span>
            <span className="hidden sm:inline">{timeDisplay.desktop}</span>
          </p>
          <div className="flex flex-col space-y-1 min-w-[80px] sm:min-w-[90px] flex-shrink-0">
            <div className="relative flex items-center">
              <div className="flex w-16 sm:w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden relative">
                <div
                  className="h-full"
                  style={{
                    width: `${percentage}%`,
                    background:
                      percentage <= 25
                        ? "#22c55e"
                        : percentage <= 75
                          ? `linear-gradient(to right, #22c55e 0%, #eab308 100%)`
                          : `linear-gradient(to right, #22c55e 0%, #eab308 50%, #ef4444 100%)`,
                  }}
                />
                <div
                  className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full border border-white ${indicatorColor}`}
                  style={{ left: `${percentage}%` }}
                />
              </div>
            </div>
            <div className="flex justify-between text-[9px] sm:text-[10px] text-gray-500 mt-1">
              <span>0</span>
              <span className="hidden sm:inline">{scaleLabels.middle}</span>
              <span>{scaleLabels.max}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
